import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { useMemo } from 'react'

import { useBillingConfig } from '@/api/queries'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

import { stripeFor } from './stripe'

/**
 * Stripe Embedded Checkout, mounted inside our page (BI2): the browser never leaves our origin. The
 * session's `return_url` brings the user back to `/app/plan?checkout=complete&session_id=...` (or
 * `/app/billing?topup=complete&...`); nothing is granted here, the webhook does that.
 */
export function EmbeddedCheckoutDialog({
  clientSecret,
  title,
  description,
  onComplete,
  onClose,
}: {
  clientSecret: string
  title: string
  description: string
  /** Stripe confirmed the payment inside the iframe: the caller moves to its return view. */
  onComplete: () => void
  onClose: () => void
}) {
  const config = useBillingConfig()
  const key = config.data?.publishable_key ?? null
  const stripe = useMemo(() => (key === null ? null : stripeFor(key)), [key])

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-2xl" data-testid="embedded-checkout">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {config.isError || (config.isSuccess && key === null) ? (
          <Alert variant="destructive">
            <AlertDescription>Checkout is not available right now. Try again shortly.</AlertDescription>
          </Alert>
        ) : stripe === null ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret, onComplete }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
      </DialogContent>
    </Dialog>
  )
}
